import { useState } from 'react';
import { submitAnalysisFeedback } from '../services/api';
import type { AnalysisFeedbackValue } from '../../shared/types';

// ─────────────────────────────────────────────────────────────
//  AnalysisFeedback
// ─────────────────────────────────────────────────────────────
// Thumbs up/down shown below the analysis result. Forwarded to
// Langfuse as a BOOLEAN score on the trace, so we can measure
// perceived quality per model/pipeline version over time. The
// optional comment lets users give the reason a verdict felt
// wrong — crucial for diagnosing model drift when Poe or Mistral
// ship a new checkpoint.

interface Props {
  traceId: string;
}

export default function AnalysisFeedback({ traceId }: Props) {
  const [submitted, setSubmitted] = useState<AnalysisFeedbackValue | null>(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const send = async (value: AnalysisFeedbackValue, withComment?: string) => {
    setSending(true);
    setError(null);
    try {
      await submitAnalysisFeedback({ traceId, value, comment: withComment });
      setSubmitted(value);
      if (withComment) setShowComment(false);
    } catch (err) {
      setError((err as Error).message || 'Échec de l\'envoi du feedback');
    } finally {
      setSending(false);
    }
  };

  const handleClick = (value: AnalysisFeedbackValue) => {
    if (submitted) return;
    // Thumbs-down opens the comment box so we capture the "why".
    if (value === 'negative') {
      setShowComment(true);
      send(value);
    } else {
      send(value);
    }
  };

  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-cream-card/60 p-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="text-sm text-slate-600">
          <p className="font-medium text-slate-800 mb-0.5">
            Cette analyse vous a-t-elle été utile&nbsp;?
          </p>
          <p className="text-xs text-slate-500">
            Votre retour alimente l'évaluation continue du moteur.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            disabled={!!submitted || sending}
            onClick={() => handleClick('positive')}
            aria-label="Analyse pertinente"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              submitted === 'positive'
                ? 'border-ink bg-emerald-200 text-ink'
                : 'border-slate-300 text-slate-700 hover:bg-emerald-50'
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 10v12" />
              <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7V10l4.34-9.34A1.5 1.5 0 0 1 14 2.34 1.5 1.5 0 0 1 15 3.88z" />
            </svg>
            Pertinent
          </button>
          <button
            type="button"
            disabled={!!submitted || sending}
            onClick={() => handleClick('negative')}
            aria-label="Analyse à améliorer"
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              submitted === 'negative'
                ? 'border-ink bg-red-200 text-ink'
                : 'border-slate-300 text-slate-700 hover:bg-red-50'
            } disabled:opacity-60 disabled:cursor-not-allowed`}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 14V2" />
              <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17v12l-4.34 9.34a1.5 1.5 0 0 1-2.66-.32 1.5 1.5 0 0 1-1-1.9z" />
            </svg>
            À améliorer
          </button>
        </div>
      </div>

      {showComment && submitted === 'negative' && (
        <form
          className="mt-4 flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!comment.trim()) return;
            send('negative', comment.trim());
          }}
        >
          <label className="text-xs text-slate-500">
            Qu'est-ce qui n'allait pas&nbsp;? (optionnel, 1000 caractères max)
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value.slice(0, 1000))}
            rows={3}
            className="w-full rounded-lg border border-slate-300 bg-cream-app px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-500"
            placeholder="Ex : verdict trop strict, jurisprudence mal interprétée, article manquant…"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowComment(false)}
              className="text-xs text-slate-500 hover:text-slate-700"
            >
              Ignorer
            </button>
            <button
              type="submit"
              disabled={sending || !comment.trim()}
              className="px-3 py-1.5 rounded-md text-xs font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Envoyer
            </button>
          </div>
        </form>
      )}

      {submitted && !showComment && (
        <p className="mt-3 text-xs text-slate-500">
          Merci, votre retour a bien été enregistré.
        </p>
      )}

      {error && (
        <p className="mt-3 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
}
